import { useRef, type ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";

const DEFAULT_THRESHOLD = 50;

/**
 * Windowed column list — renders a plain column when item count is small, otherwise virtualizes
 * scroll for stable FPS on long lists (mobile-friendly).
 */
export function VirtualizedColumn<T>({
  items,
  renderItem,
  getKey,
  threshold = DEFAULT_THRESHOLD,
  className,
  listClassName,
  maxHeightClassName = "max-h-[min(70vh,900px)]",
  estimateSize = 200,
}: {
  items: readonly T[];
  renderItem: (item: T, index: number) => ReactNode;
  getKey?: (item: T, index: number) => string | number;
  threshold?: number;
  className?: string;
  listClassName?: string;
  maxHeightClassName?: string;
  estimateSize?: number;
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan: 4,
  });

  if (items.length <= threshold) {
    return (
      <div className={cn("space-y-4", className)}>
        {items.map((item, index) => (
          <div
            key={getKey ? String(getKey(item, index)) : index}
            className={listClassName}
          >
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className={cn(maxHeightClassName, "overflow-auto rounded-md", className)}
    >
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((vi) => (
          <div
            key={vi.key}
            data-index={vi.index}
            ref={virtualizer.measureElement}
            className={cn("absolute left-0 top-0 w-full pb-4", listClassName)}
            style={{ transform: `translateY(${vi.start}px)` }}
          >
            {renderItem(items[vi.index], vi.index)}
          </div>
        ))}
      </div>
    </div>
  );
}
